import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import TagManager from 'react-gtm-module';

import ErrorPage from 'pages/ErrorPage';
import HomeComponent from 'pages/HomePage';
import NotFoundPage from 'pages/NotFoundPage';
import RedirectionPage from 'pages/RedirectionPage';
import SplashScreen from 'pages/SplashScreen';
import WelcomePage from 'pages/WelcomePage';
import WelcomeComponent from 'pages/SetPasswordPage/welcome';
import Authentication from 'pages/Authentication';
import MfaSetupPage from 'pages/Authentication/MfaSetupPage';
import MfaTotpPage from 'pages/Authentication/MfaTotpPage';
import ChangePasswordPage from 'pages/Authentication/ChangePasswordPage';
import SetPasswordPage from 'pages/Authentication/SetPasswordPage';
import InvitaionModal from 'pages/Authentication/InvitaionModal';
import { getUserData } from 'redux/UserSlice';
import ModelOverviewPage from 'pages/ModelOverviewPage';
import ProtectedRoutes from 'utils/ProtectedRoutes';
// import PlanPageOutputOptimization from 'pages/PlanPageOutputOptimization';
import PlanPageDetailsOPZ from 'pages/PlanPageDetailsOPZ';
// import PlanPageDetailsOPZRevamp from 'pages/PlanPageDetailsOPZRevamp';
import { ModalOverViewPageHeaderV1 } from 'pages/ModalOverViewPageHeaderV1';
// V2 ("Zoho-inspired") header. A fork rather than edits to V1, because V1's
// pieces are also imported by components/HeaderV1 and the Plan page's
// NewHeader — restyling it in place would have restyled those too.
import { ModalOverViewPageHeaderRevamp } from 'pages/ModalOverViewPageHeaderRevamp';
import { isUiRevampEnabled } from 'utils/uiRevampFlag';
import { Flags } from 'models/Global.interface';
import PlanRevamp from 'pages/PlanRevamp';
import TaskInbox from 'pages/Tasks/TaskInbox';
import TaskPage from 'pages/Tasks/TaskPage';
import { isWorkflowTasksEnabled } from 'utils/workflowTasksFlag';

interface NavigationProperties {
  flags?: Flags;
}

/**
 * The header follows whichever body is being rendered, off the one flag.
 *
 * It used to read `dimensionsRevampV2`, a second flag that defaults to on. In
 * any environment where that flag hasn't been created — LaunchDarkly has no
 * opinion, so the default applies — the revamp header was drawn above the
 * original body: light chrome over the original UI, with its white text
 * invisible against it. One switch is what the V2 flag's own docs said should
 * drive both.
 */
const ModelHeader = ({ flags }: { flags?: Flags }) => {
  const useRevampHeader = Boolean(flags?.uiRevamp);

  return useRevampHeader ? <ModalOverViewPageHeaderRevamp /> : <ModalOverViewPageHeaderV1 />;
};

const ModelHomeRedirect = () => {
  const { modelId } = useParams();

  if (!modelId) {
    return <Navigate to='/home' replace />;
  }

  return <Navigate to={`/model/${modelId}/dashboard`} replace />;
};

const ModelTabRedirect = () => {
  const { modelId, tab } = useParams();
  const tabMap: Record<string, string> = {
    dashboard: 'dashboard',
    blocks: 'blocks',
    dimensions: 'dimensions',
    data: 'data',
    import: 'data',
  };

  if (!modelId) {
    return <Navigate to='/home' replace />;
  }

  const normalizedTab = tabMap[tab || ''] ?? 'dashboard';
  return <Navigate to={`/model/${modelId}/${normalizedTab}`} replace />;
};

const Navigation = ({ flags }: NavigationProperties) => {
  const { REACT_APP_GTM_ID }: any = process.env;
  const location = useLocation();
  const user = useSelector(getUserData);

  useEffect(() => {
    if (user) {
      const tagManagerArguments = {
        gtmId: REACT_APP_GTM_ID,
        events: {
          userId: user.id,
        },
      };
      TagManager.initialize(tagManagerArguments);
    }
  }, [location.pathname]);

  return (
    <Routes>
      <Route index element={<Authentication type={1} />} />
      <Route path='signup' element={<Authentication type={2} />} />
      <Route path='mfa/setup' element={<MfaSetupPage />} />
      <Route path='mfa/totp' element={<MfaTotpPage />} />
      <Route path='create/:modelId' element={<SplashScreen />} />
      <Route
        path='welcome'
        element={
          <ProtectedRoutes>
            <WelcomePage />
          </ProtectedRoutes>
        }
      />
      <Route path='change-password' element={<ChangePasswordPage />} />
      <Route path='set-password' element={<SetPasswordPage />} />
      <Route path='set-new-password' element={<SetPasswordPage />} />
      <Route path='user/welcome' element={<WelcomeComponent />} />
      <Route
        path='home'
        element={
          <ProtectedRoutes>
            <HomeComponent />
          </ProtectedRoutes>
        }
      />
      <Route path='share-model/:token/:emailToken' element={<RedirectionPage />} />
      <Route path='share-model/:token' element={<RedirectionPage />} />

      <Route
        path='model/:modelId'
        element={
          <ProtectedRoutes>
            <Navigate to='dashboard' replace />
          </ProtectedRoutes>
        }
      />
      <Route
        path='model/:modelId/:tab'
        element={
          <ProtectedRoutes>
            {/* One switch for the whole V2 look: this picks the header, and
                each header renders its own matching Dimensions body, so the
                two can never end up mismatched. Default is off — see
                utils/dimensionsRevampV2Flag. */}
            {isUiRevampEnabled(flags) ? <ModelHeader flags={flags} /> : <ModelOverviewPage />}
          </ProtectedRoutes>
        }
      />
      <Route
        path='model/:modelId/tab=:tab'
        element={
          <ProtectedRoutes>
            <ModelTabRedirect />
          </ProtectedRoutes>
        }
      />
      <Route
        path='model/:modelId/home'
        element={
          <ProtectedRoutes>
            {isUiRevampEnabled(flags) ? <ModelHomeRedirect /> : <ModelOverviewPage />}
          </ProtectedRoutes>
        }
      />
      <Route
        path='model/:modelId/block/:blockId/plan'
        element={
          <ProtectedRoutes>
            {!flags?.planPageUiRevamp ? (
              <PlanPageDetailsOPZ flags={flags as Flags} />
            ) : (
              <PlanRevamp flags={flags as Flags} />
            )}
          </ProtectedRoutes>
        }
      />
      <Route
        path='/error'
        element={
          <ProtectedRoutes>
            <ErrorPage />
          </ProtectedRoutes>
        }
      />
      <Route
        path='tasks'
        element={
          <ProtectedRoutes>
            {isWorkflowTasksEnabled(flags) ? <TaskInbox /> : <NotFoundPage />}
          </ProtectedRoutes>
        }
      />
      <Route
        path='tasks/:taskId'
        element={
          <ProtectedRoutes>
            {isWorkflowTasksEnabled(flags) ? <TaskPage /> : <NotFoundPage />}
          </ProtectedRoutes>
        }
      />
      <Route path='/invitation/:token' element={<InvitaionModal />} />
      <Route path='/*' element={<NotFoundPage />} />
    </Routes>
  );
};

export default withLDConsumer()(Navigation);
